import { CareOSLoader } from '@/components/ui/careos-loader';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { captureEvent } from '@/lib/posthog';
import { track, trackSignupCompletedOnce } from '@/lib/analytics';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export const PENDING_COMPANY_INVITE_KEY = 'careos_pending_company_invite';

interface InviteInfo {
  company_name: string;
  role: string;
  masked_email: string;
  expires_at: string;
  accepted_at: string | null;
}

export default function AcceptCompanyInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      loadInviteInfo();
    }
  }, [token]);

  const loadInviteInfo = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_company_invitation_by_token', { invitation_token: token! });

      if (error) throw error;

      if (!data || data.length === 0) {
        setError(t('company.invite.invalidLink'));
        return;
      }

      const info = data[0] as InviteInfo;

      if (info.accepted_at) {
        setError(t('company.invite.inviteAlreadyAccepted'));
        return;
      }

      if (new Date(info.expires_at) < new Date()) {
        setError(t('company.invite.inviteExpired'));
        return;
      }

      setInviteInfo(info);
    } catch (err) {
      console.error('Error loading company invite:', err);
      setError(t('company.invite.invalidLink'));
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'coordinator': return t('company.invite.roleCoordinator');
      case 'employee': return t('company.invite.roleCaregiver');
      default: return role;
    }
  };

  const acceptInvitation = async () => {
    if (!user || !token) return;

    setIsAccepting(true);
    try {
      const { data: success, error } = await supabase
        .rpc('accept_company_invitation', { invitation_token: token });

      if (error) {
        if (error.message?.includes('EMAIL_MISMATCH')) {
          toast.error(t('company.invite.emailMismatch'));
          return;
        }
        throw error;
      }

      if (!success) {
        toast.error(t('company.invite.invalidLink'));
        return;
      }

      sessionStorage.removeItem(PENDING_COMPANY_INVITE_KEY);

      toast.success(t('company.invite.acceptSuccess'));

      captureEvent('company_invite_accepted', {
        token_prefix: token.substring(0, 8),
      });
      // `company_member_joined` is emitted server-side (trigger on company_members).
      trackSignupCompletedOnce(user, { audience: 'company' });


      navigate('/app/company');
    } catch (err: any) {
      console.error('Error accepting company invitation:', err);
      toast.error(t('company.invite.acceptError'));
    } finally {
      setIsAccepting(false);
    }
  };

  // Loading state
  if (isLoading || authLoading) {
    return (
      <CareOSLoader fullScreen label={t('common.loading')} />
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{t('invite.ops')}</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/app')}>
              {t('invite.goToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    const handleGoToAuth = () => {
      if (token) {
        sessionStorage.setItem(PENDING_COMPANY_INVITE_KEY, token);
      }
      const returnUrl = `/invite/company/${token}`;
      navigate(`/auth?returnTo=${encodeURIComponent(returnUrl)}`);
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-primary-foreground" />
            </div>
            <h2 className="text-xl font-bold mb-2">
              {t('company.invite.joinTitle')}
            </h2>
            {inviteInfo && (
              <div className="mb-4 space-y-1">
                <p className="text-foreground font-medium">{inviteInfo.company_name}</p>
                <p className="text-sm text-muted-foreground">
                  {getRoleLabel(inviteInfo.role)}
                </p>
              </div>
            )}
            <p className="text-muted-foreground mb-4">
              {t('company.invite.joinDescription')}
            </p>
            {inviteInfo?.masked_email && (
              <p className="text-xs text-muted-foreground mb-4">
                {t('company.invite.emailMismatchHint', { email: inviteInfo.masked_email })}
              </p>
            )}
            <Button className="w-full" onClick={handleGoToAuth}>
              {t('invite.loginToAccept')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged in - show accept
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center">
          <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">
            {t('company.invite.acceptTitle')}
          </h2>
          {inviteInfo && (
            <p className="text-muted-foreground mb-4">
              {t('company.invite.acceptDescription', {
                company: inviteInfo.company_name,
                role: getRoleLabel(inviteInfo.role),
              })}
            </p>
          )}
          {inviteInfo?.masked_email && (
            <p className="text-xs text-muted-foreground mb-4">
              {t('company.invite.emailMismatchHint', { email: inviteInfo.masked_email })}
            </p>
          )}

          <div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3 mb-4 text-left">
            <p>
              <strong>Sobre o Contrato B2B:</strong> o Contrato de Prestação de
              Serviços e o DPA foram firmados pelo representante legal da empresa.
              Ao aceitar este convite, você passa a colaborar dentro dessa empresa
              e fica sujeito aos Termos de Uso da plataforma.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/app')}
            >
              {t('invite.decline')}
            </Button>
            <Button
              className="flex-1"
              onClick={acceptInvitation}
              disabled={isAccepting}
            >
              {isAccepting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t('invite.accept')
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
